// frontend/src/api/client.js

// IMPORTANT:
// - En PROD/STAGING : on ne définit PAS REACT_APP_API_BASE_URL => BASE="" => appels sur "/api/..." (same-origin)
// - En LOCAL (React dev server) : REACT_APP_API_BASE_URL=http://localhost:8000

const RAW_BASE = (process.env.REACT_APP_API_BASE_URL || "").trim();
const BASE = RAW_BASE ? RAW_BASE.replace(/\/$/, "") : "";

function normalizePath(path) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

// Récupération des tokens depuis le localStorage
function getAccess() {
  return (
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access") ||
    ""
  );
}

function getRefresh() {
  return (
    localStorage.getItem("refreshToken") ||
    localStorage.getItem("refresh") ||
    ""
  );
}

// Fonction bas niveau qui fait un appel fetch + gestion du refresh
async function request(path, options = {}) {
  const url = `${BASE}${normalizePath(path)}`;

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Injecter l'access token si disponible
  const access = getAccess();
  if (access) {
    headers.Authorization = `Bearer ${access}`;
  }

  let res = await fetch(url, { ...options, headers });

  // 401 → on tente un refresh
  if (res.status === 401) {
    const refreshTok = getRefresh();
    if (refreshTok) {
      const refreshUrl = `${BASE}/api/auth/token/refresh/`;
      const r = await fetch(refreshUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshTok }),
      });

      if (r.ok) {
        const tokenData = await r.json();
        if (tokenData.access) {
          localStorage.setItem("access", tokenData.access);
          localStorage.setItem("accessToken", tokenData.access);
          headers.Authorization = `Bearer ${tokenData.access}`;
        }
        if (tokenData.refresh) {
          localStorage.setItem("refresh", tokenData.refresh);
          localStorage.setItem("refreshToken", tokenData.refresh);
        }
        // On rejoue la requête avec le nouveau token
        res = await fetch(url, { ...options, headers });
      }
    }
  }

  // ⚠️ FIX : parse le JSON et retourne { data } comme axios.
  // Avant ce fix, la Response brute était retournée directement,
  // ce qui rendait "data" undefined dans tous les .then(({ data }) => ...)
  // et crashait tous les jeux (data.items, data.words, etc.)
  const contentType = res.headers.get("content-type") || "";

  if (!contentType.includes("application/json")) {
    // Réponse non-JSON (HTML, texte...) → erreur claire
    const text = await res.text();
    if (!res.ok) throw new Error(`HTTP ${res.status} — réponse non-JSON reçue`);
    return { data: text, status: res.status, ok: res.ok };
  }

  let data = null;
  try { data = await res.json(); } catch { data = null; }

  if (!res.ok) {
    const message = data?.detail || data?.message || data?.error || `HTTP ${res.status}`;
    throw new Error(message);
  }

  return { data, status: res.status, ok: res.ok };
}

// Petit helper façon axios
function api(path, options) {
  return request(path, options);
}

api.get = (path, options = {}) => request(path, { ...options, method: "GET" });

api.post = (path, body = {}, options = {}) =>
  request(path, {
    ...options,
    method: "POST",
    body: JSON.stringify(body),
  });

api.patch = (path, body = {}, options = {}) =>
  request(path, {
    ...options,
    method: "PATCH",
    body: JSON.stringify(body),
  });

api.delete = (path, options = {}) =>
  request(path, { ...options, method: "DELETE" });

export default api;
export { api, BASE };